import React, { useEffect, useState, useCallback } from "react";
import { Contact, Search, UserRoundPlus } from "lucide-react";
import { changePatientStatus, exportPatients, fetchPatients, changePatientLocationStatus } from "../../services/patient";
import { generatePatientTableData, notify, getPractice, numberWithCommas, can_visit, getSpeciality, userMessageTemplates, getDateString } from "../../helpers";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import { addToWaitingRoom } from "../../services/patients";
import { fetchFilterData } from "../../services/saveFilter";
import { useNavigate } from "react-router-dom";
import debounce from 'lodash.debounce';
import FilterSaveConfirmationModal from "../Modal/FilterSaveConfirmationModal";
import SaveFilter from "../Filter/SaveFilter";
import DataTablePagination from "../DataTablePagination";
import Filter from "../Filter";
import FilterSummary from "../UI/FilterSummary";
import Spacing from "../Spacing";
import PatientDataTable from "../DataTable/PatientDataTable";
import AddPatientModal from "../Modal/AddPatientModal";


export default function PatientDatabase({
  selectedPracticeChange,
  fetchChangePracticeData,
  smsLimit,
  setSmsLimit,
  setLetterLimit,
  changePracticeSpeciality,
  waitingRoomProviders
}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  let user_id = getPractice("user_id");
  let specilityId = getSpeciality()?.id || 32;
  if (selectedPracticeChange != null && fetchChangePracticeData) {
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
    specilityId = selectedPracticeChange?.specialities[0]?.id || 32;
  }
  if (changePracticeSpeciality != null) {
    specilityId = changePracticeSpeciality?.id || 32;
  }
  let perPage = 10;
  if(can_visit('ehr_veradigm')) { specilityId = 32; perPage = 20; }
  let dateStr = getDateString();

  const navigate = useNavigate();
  const [patientAdd, setPatientAdd] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [messageTemplate] = useState(userMessageTemplates());
  const [data, setData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedValue, setSelectedValue] = useState(2);
  const defaultFilter = {
    account_id: account_id,
    location_id: location_id,
    speciality_id: specilityId,
    page: 1,
    per_page: perPage || 10,
    search_term: "",
    first_name: "",
    last_name: "",
    dob: "",
    sex: "",
    mobile_phone: "",
    start_age: "",
    end_age: "",
    insurance_ids: [],
    last_visit: "",
    visit_day_query_type: "",
    visited_last_days: "",
    not_visited_last_days: "",
    risk_level: "",
    preventative_due: "",
    order_by: [],
    city: "",
    state: "",
    zip: "",
    patient_type: selectedValue,
  };
  const [filter, setFilter] = useState(defaultFilter);

  const [selectedInsuranceOptions, setSelectedInsuranceOptions] = useState([]);
  const [patientFormData, setPatientFormData] = useState(filter);
  const [isFilterCleared, setIsFilterCleared] = useState(false);
  const [globalCheckedIds, setGlobalCheckedIds] = useState(new Set());
  const [selectedPatientData, setSelectedPatientData] = useState([]);
  const [showSave, setShowSave] = useState(true);
  const [saveConfirmModal, setSaveConfirmModal] = useState(false);
  const [filterData, setFilterData] = useState([]);
  const [changeLocationLoader, setChangeLocationLoader] = useState(false);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  const handleClearFilters = () => {
    setIsFilterCleared(true);
  };

  const handleResetFilterStatus = () => {
    setIsFilterCleared(false);
  };

  const [dataCount, setDataCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  const changeStatusDefaultValues = {
    account_id: account_id,
    location_id: location_id,
    patient_ids: [],
    status: "",
  };

  const changeLocationStatusDefaultValues = {
    new_location_id: "",
    account_id: account_id,
    patient_ids: []
  };

  const [changeStatusData, setChangeStatusData] = useState(
    changeStatusDefaultValues
  );
  const [changeLocationStatusData, setChangeLocationStatusData] = useState(
    changeLocationStatusDefaultValues
  );

  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);

  const loadData = async (getFilter) => {
    setFilter(getFilter);
    let isCanceled = false;

    try {
      const response = await fetchPatients(getFilter);
      if (response) {
        const generateData = generatePatientTableData(response?.data?.results);
        setData(generateData);
        setDataCount({
          totalCount: response?.data?.count,
          totalPages: response?.data?.total_pages,
        });
        if (checkedIds.length < 1) {
          setSelectAll(false);
        }
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadPatientData ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  useEffect(() => {
    loadData(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleCheckboxChange = (id, rowData) => {
    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
    setGlobalCheckedIds((prevCheckedIds) => {
      const updatedCheckedIds = new Set(prevCheckedIds);
      if (updatedCheckedIds.has(id)) {
        updatedCheckedIds.delete(id);
        setSelectedPatientData((prevData) => prevData.filter((data) => data.id !== id));
      } else {
        updatedCheckedIds.add(id);
        setSelectedPatientData((prevData) => [...prevData, rowData]);
      }
      return updatedCheckedIds;
    });
  };

  const handleSelectAll = (currentPageData) => {
    if (selectAll) {
      const newCheckedIds = checkedIds.filter(
        (id) => !currentPageData.some((row) => row.id === id)
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.delete(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) =>
        prevData.filter((data) => !currentPageData.some((row) => row.id === data.id))
      );
    } else {
      const newCheckedIds = Array.from(
        new Set([...checkedIds, ...currentPageData.map((row) => row.id)])
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.add(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) => [
        ...prevData,
        ...currentPageData.filter(
          (row) => !prevData.some((data) => data.id === row.id)
        ),
      ]);
    }

    setSelectAll(!selectAll);

    setData((prevData) =>
      prevData.map((row) => ({
        ...row,
        checked: !selectAll,
      }))
    );
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setFilter((prevFilter) => ({ ...prevFilter, search_term: value, page: 1 }));
    }, 600), // 600ms debounce delay
    []
  );

  // Handle input change
  const handleChangeSearchTerm = (event) => {
    const value = event.target.value;    
    setDataLoading(true);
    debouncedSearch(value);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  useEffect(() => {
    setCheckSelected(checkedIds.length > 0 ? true : false);
  }, [checkedIds]);

  const handleAddToWaitingRoom = async (data, id) => {
    const postDataObject = {
      account_id: account_id,
      location_id: location_id,
      speciality_id: specilityId,
      patient_id: data.id,
      dos: dateStr,
      care_gap: [],
      review_sent: false,
      provider_id: id
    };

    try {
      await addToWaitingRoom(postDataObject);
      notify("success", "Patient added to waiting room successfully");
    } catch (error) {
      notify("error", error?.response?.data?.detail);
    }
  };

  const handlePatientStatus = async (e) => {
    setDataLoading(true);
    const { name, value } = e.target;
    let parsedValue = value;

    if (name === "status") {
      parsedValue = parseInt(value);
    }
    const newChangeStatusData = {
      ...changeStatusData,
      [name]: parsedValue,
      patient_ids: checkedIds,
    };

    setChangeStatusData(newChangeStatusData);

    const response = await changePatientStatus(newChangeStatusData);
    if (response.detail) {
      notify("error", response.detail);
    } else {
      notify("success", response.data.message);
      setSelectedPatientData([]);
      setGlobalCheckedIds(new Set());
      loadData(filter);
    }
  };

  const handlePatientLocationStatus = async (e) => {
    const { name, value } = e.target;
    let parsedID = value;
    if (name === "bulk_location_change") {
      parsedID = parseInt(parsedID);
    }
    
    const newChangeLocationData = {
      ...changeLocationStatusData,
      new_location_id: parsedID,
      account_id: account_id,
      patient_ids: checkedIds
    };
    setChangeLocationStatusData(newChangeLocationData);

    setDataLoading(true);
    setChangeLocationLoader(true);

    try {
      const response = await changePatientLocationStatus(newChangeLocationData);
      if(response) {
        if (response?.detail) {
          notify("error", response.detail);
        } else {
          if(response?.data?.status === "success") {
            notify("success", response?.data?.message);
            setChangeLocationStatusData({
              ...changeLocationStatusDefaultValues,
              new_location_id: "",
            });
            setSelectedPatientData([]);
            setGlobalCheckedIds(new Set());
            loadData(filter);
          } else {
            notify("error", response?.data?.message);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    } finally {
      setChangeLocationLoader(false);
      setDataLoading(false);
    }
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;
    const sameSpec = filter.speciality_id === specilityId;

    if (sameLocation && sameAccount && sameSpec) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
      speciality_id: specilityId,
      page: 1,
    });
    setCheckedIds([]);
    setSelectedPatientData([]);
    setGlobalCheckedIds(new Set());
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id, specilityId]);

  const handleFilterCheckboxChange = (value) => {
    if(value === filter?.patient_type) {
      return;
    }
    setSelectedValue(value);
    setDataLoading(true);
    setFilter((prevFilter) => ({
      ...prevFilter,
      patient_type: value,
      page: 1,
    }));
    setPatientFormData({ ...filter, patient_type: value, page: 1 });
  };

  const checkboxes = [
    { value: 1, label: "All", tooltip: "All Patients" },
    { value: 2, label: "Active", tooltip: "Active Patients Only" },
    { value: 3, label: "Inactive", tooltip: "Inactive Patients Only" },
    { value: 4, label: "Self Pay", tooltip: "Self Pay Patients Only" },
  ];

  const handleExportAll = async (e) => {
    setButtonLoader(true);
    try {
      const response = await exportPatients(filter);

      if (response && response.data) {
        const responseData = response.data;
        const dataBlob = new Blob([responseData], {
          type: "text/csv;charset=utf-8;",
        });
        const generateURL = window.URL.createObjectURL(dataBlob);

        // File Name Generate
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const seconds = String(now.getSeconds()).padStart(2, "0");
        const filename = `Patient_Data_${year}_${month}_${day}_${hours}_${minutes}_${seconds}.csv`;

        const link = document.createElement("a");
        link.href = generateURL;
        link.setAttribute("download", filename);

        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        window.URL.revokeObjectURL(generateURL);
      }
    } catch (error) {
      // console.log("🚀 ~ ExporPatients ~ error:", error);
    } finally {
      setButtonLoader(false);
    }
  };

  useEffect(() => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    if (params.has('type') && params.get('type') === 'add') {
      setPatientAdd(true);
    }
  }, []);

  const clearFilterField = (field) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      [field]: "",  // Clear the specified field in filter state
    }));
    setPatientFormData((prevData) => ({
      ...prevData,
      [field]: "",  // Clear the specified field in patientFormData
    }));
  };

  const loadFilters = async () => {
    const filters = {
      account_id: account_id,
      location_id: location_id,
      user_id: user_id,
      filter_type: "patient-database",
    };

    try {
      const response = await fetchFilterData(filters);
      if (response) {
        if (response?.detail) {
          notify("error", response?.detail);
        } else {
          setFilterData(response?.data);
        }
      }
    } catch (error) {
      // console.log("🚀 ~ loadFilters ~ error:", error);
    }
  };

  useEffect(() => {
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account_id, location_id]);

  const handleReferralNavigation = (data) => {
    navigate(`/new-referral`, { state: { data } });
  };

  useDocumentTitle("Patient Database");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i className="docpro_teal_bg docpro_white_color">
            <Contact width={20} height={20} />
          </i>
          Patient Database
          <span>{numberWithCommas(dataCount.totalCount)}</span>
        </h2>
        <div className="docpro_page_heading_btns">
          {can_visit("patient_database_create_patient") && (
            <button
              className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn docpro_sm_btn"
              onClick={() => {
                setPatientAdd(!patientAdd);
                setPreviousScrollPosition(window.scrollY);
                window.scrollTo(0, 0);
              }}
            >
              <UserRoundPlus width={20} height={20} />
              <span className="text">Add Patient</span>
            </button>
          )}
          {/* <button className="docpro_bordered_btn docpro_fw_600 docpro_primary_color">
            <FilePlus2 width={20} height={20} />
            <span>Import</span>
          </button> */}
        </div>
      </div>
      <Spacing lg="5" sm="5" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings small_heading">
          <div className="searching_div">
            <form className="docpro_search_form width_100">
              <input
                type="search"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>

            <div className="filter_type small_filter_type">
              {checkboxes.map((checkbox) => (
                <div
                  key={checkbox.value}
                  className="docpro_checkbox docpro_tip_checkbox"
                  style={{ marginLeft: checkbox.value === 1 ? 0 : "12px" }}
                >
                  <input
                    type="checkbox"
                    name="patient_type"
                    value={checkbox.value}
                    checked={selectedValue === checkbox.value}
                    onChange={() => handleFilterCheckboxChange(checkbox.value)}
                  />
                  <label style={{ marginLeft: "5px" }}>{checkbox.label}</label>
                  <span className="docpro_tip">{checkbox.tooltip}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="docpro_data_table_btns small_filter_btns p-0">
            {filterData?.length > 0 && (
              <SaveFilter
                account_id={account_id}
                location_id={location_id}
                user_id={user_id}
                filterData={filterData}
                setDataLoading={setDataLoading}
                setPatientFormData={setPatientFormData}
                setSelectedInsuranceOptions={setSelectedInsuranceOptions}
                handleFilterCheckboxChange={handleFilterCheckboxChange}
                setFilter={setFilter}
                setShowSave={setShowSave}
                loadFilters={loadFilters}
                reloadData={loadData}
                reloadDataFilter={defaultFilter}
                previousScrollPosition={previousScrollPosition}
                setPreviousScrollPosition={setPreviousScrollPosition}
              />
            )}
            <Filter
              filterType="patient"
              reloadData={loadData}
              reloadDataFilter={filter}
              setDataLoading={setDataLoading}
              isFilterCleared={isFilterCleared}
              onResetFilterStatus={handleResetFilterStatus}
              selectedInsuranceOptions={selectedInsuranceOptions}
              setSelectedInsuranceOptions={setSelectedInsuranceOptions}
              patientFormData={patientFormData}
              setPatientFormData={setPatientFormData}
              setShowSave={setShowSave}
            />
          </div>
        </div>

        <div className="docpro_filter_summary">
          <FilterSummary 
            filter={filter} 
            setFilter={setFilter}
            loadData={loadData}
            onClearFilters={handleClearFilters}
            setSelectedInsuranceOptions={setSelectedInsuranceOptions}
            clearFilterField={clearFilterField}
            patientFormData={patientFormData}
            setPatientFormData={setPatientFormData}
            setSaveConfirmModal={setSaveConfirmModal}
            showSave={showSave}
            setShowSave={setShowSave}
            setDataLoading={setDataLoading}
          /> 
        </div>
        <PatientDataTable
          tableData={data}
          handleSelectAll={() => handleSelectAll(data)}
          handleCheckboxChange={handleCheckboxChange}
          checkedIds={checkedIds}
          setCheckedIds={setCheckedIds}
          checkSelected={checkSelected}
          handleAddToWaitingRoom={handleAddToWaitingRoom}
          reloadData={loadData}
          reloadDataFilter={filter}
          messageTemplate={messageTemplate}
          handlePatientStatus={handlePatientStatus}
          handlePatientLocationStatus={handlePatientLocationStatus}
          user_id={user_id}
          account_id={account_id}
          location_id={location_id}
          smsLimit={smsLimit}
          setSmsLimit={setSmsLimit}
          setLetterLimit={setLetterLimit}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          exportData={handleExportAll}
          buttonLoader={buttonLoader}
          globalCheckedIds={globalCheckedIds}
          setGlobalCheckedIds={setGlobalCheckedIds}
          selectedPatientData={selectedPatientData}
          setSelectedPatientData={setSelectedPatientData}
          handleReferralNavigation={handleReferralNavigation}
          changeLocationLoader={changeLocationLoader}
          waitingRoomProviders={waitingRoomProviders}
          specilityId={specilityId}
        />
        {data.length > 0 && !dataLoading && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={dataCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={dataCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {patientAdd && (
        <AddPatientModal
          reloadData={loadData}
          reloadDataFilter={filter}
          account_id={account_id}
          location_id={location_id}
          setSelectedValue={setSelectedValue}
          setFilter={setFilter}
          patientAdd={patientAdd}
          setPatientAdd={setPatientAdd}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {saveConfirmModal && (
        <FilterSaveConfirmationModal
          account_id={account_id}
          location_id={location_id}
          user_id={user_id}
          filter_type="patient-database"
          filter={filter}
          openModal={saveConfirmModal}
          setOpenModal={setSaveConfirmModal}
          setShowSave={setShowSave}
          loadFilters={loadFilters}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
